import { useState, useMemo, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Printer, X } from "lucide-react";
import { speciesOrFirstWord } from "@/lib/species";

const escHtml = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

function formatTime(minutes) {
  if (minutes < 1) return `${Math.round(minutes * 60)} seconds`;
  if (minutes >= 60) {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.round(minutes % 60);
    return mins > 0 ? `${hrs.toLocaleString()}h ${mins}m` : `${hrs.toLocaleString()}h`;
  }
  return `${minutes.toFixed(1)} min`;
}

const formatStopwatch = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const hrs = Math.floor(s / 3600), mins = Math.floor((s % 3600) / 60), secs = s % 60;
  if (hrs > 0) return `${hrs}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const monthKeyOf = (item) => {
  const d = item.cut_date || (item.history_moved_at || "").slice(0, 10);
  if (!d || d.length < 7) return null;
  return d.slice(0, 7);
};

const monthLabel = (key) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
};

const fmtDate = (d) => {
  if (!d) return "—";
  const [y, m, day] = d.split("-").map(Number);
  return `${m}/${day}/${y}`;
};

// Exact same layout/CSS as the cut list print page
const PRINT_CSS = `
  .pcc-print { font-family: Arial, sans-serif; color: #111; }
  .pcc-print h1 { font-size: 20px; margin: 0 0 4px; font-weight: bold; }
  .pcc-print .date { font-size: 12px; color: #666; margin: 0 0 16px; }
  .pcc-print table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
  .pcc-print th, .pcc-print td { border: 1px solid #ccc; padding: 4px 6px; text-align: left; font-size: 11.5px; }
  .pcc-print th { background: #222; color: #fff; font-weight: bold; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; }
  .pcc-print tr:nth-child(even) { background: #f9f9f9; }
  .pcc-print .notes { font-size: 10px; color: #555; font-style: italic; margin-top: 2px; }
  .pcc-print .lumber-line { font-size: 11px; white-space: nowrap; }
  .pcc-print .totals { margin-top: 16px; padding: 12px; background: #222; color: #fff; margin-bottom: 28px; }
  .pcc-print .totals h3 { margin: 0 0 8px; font-size: 14px; text-transform: uppercase; letter-spacing: 1px; }
  .pcc-print .totals p { margin: 4px 0; font-size: 13px; }
  .pcc-print .missing { color: #f44; font-weight: bold; }
`;

// Compact one-liner per lumber option — bold number is cants USED (or required, marked "req")
const lumberCellHtml = (item) => {
  const opts = (item.lumber || []).map((l, li) => ({ l, li })).filter(({ l }) => l?.quantity || l?.pcs_per_cant);
  const compLen = item.length || 0;
  const origTotal = item.original_total || item.total;
  const parts = opts.map(({ l, li }) => {
    const cantIn = (l.length || 0) * 12;
    const ppc = l.pcs_per_cant || (l.quantity > 0 && origTotal > 0 ? Math.round(origTotal / l.quantity) : 0) || (compLen > 0 ? Math.floor(cantIn / compLen) : 0);
    const required = ppc > 0 ? Math.round((origTotal / ppc) * 100) / 100 : Math.round((l.quantity || 0) * 100) / 100;
    const mainUsed = parseFloat(item.lumber_custom_qty?.[li] || "") || 0;
    const extraUsed = (item.lumber_extra_used?.[li] || []).reduce((s, ex) => s + (parseFloat(ex.qty || "") || 0), 0);
    const used = Math.round((mainUsed + extraUsed) * 100) / 100;
    const species = item.lumber_custom_species?.[li] || '';
    const dims = `${l.thickness ? `${l.thickness}" x ` : ''}${l.width && l.width !== 'Random' ? `${l.width}" x ` : l.width === 'Random' ? 'Rndm x ' : ''}${l.length}'`;
    const desc = l.description ? ` ${escHtml(l.description)}` : '';
    const sp = species && !(l.description || '').toLowerCase().startsWith(species.toLowerCase()) ? ` ${escHtml(species)}` : '';
    return `<div class="lumber-line"><strong>${(used > 0 ? used : required).toLocaleString()}</strong> — ${escHtml(dims)}${desc}${sp}${used > 0 ? '' : ' <em>(req)</em>'}</div>`;
  });
  (item.custom_lumber || []).forEach(cl => {
    const qty = parseFloat(cl.qty || "") || 0;
    if (!qty) return;
    const dims = `${cl.thickness ? `${cl.thickness}" x ` : ''}${cl.width && cl.width !== 'Random' ? `${cl.width}" x ` : cl.width === 'Random' ? 'Rndm x ' : ''}${cl.length ? `${cl.length}'` : ''}`;
    parts.push(`<div class="lumber-line"><strong>${(Math.round(qty * 100) / 100).toLocaleString()}</strong> — ${escHtml(dims)}${cl.species ? ` ${escHtml(cl.species)}` : ''} <em>(cust)</em></div>`);
  });
  return parts.length > 0 ? parts.join('') : '—';
};

const skidsCellHtml = (item) => {
  if (item.skids == null || item.skids <= 0) return '—';
  const w = Math.floor(item.skids);
  const r = item.boards_per_skid ? Math.round((item.skids - w) * item.boards_per_skid * 100) / 100 : 0;
  if (w > 0 && r > 0) return `${w} Skid${w > 1 ? 's' : ''} + ${r} Pcs.`;
  if (w > 0) return `${w} Skid${w > 1 ? 's' : ''}`;
  if (r > 0) return `${r} Pcs.`;
  return `${item.skids} Skids`;
};

// Full month section in the exact print-page format (table + dark totals block)
const monthHtml = (m, withRemove = false) => {
  const items = m.rawItems;
  const totalStopwatchSecs = items.reduce((sum, i) => sum + (i.stopwatch_elapsed || 0), 0);
  const totalEstMinutes = items.reduce((sum, i) => sum + (i.pcs_per_min ? (i.total || 0) / i.pcs_per_min : 0), 0);
  const totalBdFt = items.reduce((sum, i) => sum + ((i.board_footage || 0) * (i.per_pallet || 0) * (i.pallet_quantity || 0)), 0);
  const missingTime = items.filter(i => !i.pcs_per_min).length;
  const missingBdFt = items.filter(i => !i.board_footage).length;

  let rawBdFt = 0;
  const cantTotals = {};
  const addCant = (thickness, width, length, species, qty) => {
    if (qty <= 0) return;
    const key = (thickness || '') + '|' + (width || '') + '|' + length + '|' + species;
    if (!cantTotals[key]) cantTotals[key] = { thickness, width, length, species, total: 0 };
    cantTotals[key].total += qty;
  };
  items.forEach(item => {
    (item.lumber || []).forEach((l, li) => {
      if (!l?.length) return;
      const t = parseFloat(l.thickness) || 0;
      const w = l.width === 'Random' ? 6 : (parseFloat(l.width) || 6);
      const len = parseFloat(l.length) || 0;
      const mainQty = parseFloat(item.lumber_custom_qty?.[li] || "") || 0;
      const mainSpecies = item.lumber_custom_species?.[li] || speciesOrFirstWord(l.description);
      if (mainQty > 0) { rawBdFt += (mainQty * t * w * len) / 12; addCant(l.thickness, l.width, l.length, mainSpecies, mainQty); }
      (item.lumber_extra_used?.[li] || []).forEach(ex => {
        const exQty = parseFloat(ex.qty || "") || 0;
        if (exQty <= 0) return;
        rawBdFt += (exQty * t * w * len) / 12;
        addCant(l.thickness, l.width, l.length, ex.species || mainSpecies, exQty);
      });
    });
    (item.custom_lumber || []).forEach(cl => {
      const qty = parseFloat(cl.qty || "") || 0;
      if (qty <= 0) return;
      const t = parseFloat(cl.thickness) || 0;
      const w = cl.width === 'Random' ? 6 : (parseFloat(cl.width) || 6);
      const len = parseFloat(cl.length) || 0;
      rawBdFt += (qty * t * w * len) / 12;
      addCant(cl.thickness, cl.width, cl.length, cl.species || '', qty);
    });
  });
  const cantEntries = Object.values(cantTotals).sort((a, b) => (parseFloat(a.thickness) || 0) - (parseFloat(b.thickness) || 0) || (parseFloat(a.length) || 0) - (parseFloat(b.length) || 0));

  return `
<h1>Cut History — ${escHtml(m.label)}</h1>
<p class="date">${items.length} item${items.length === 1 ? '' : 's'}</p>
<table>
  <thead><tr><th>Component</th><th>Qty</th><th>Est. Time</th><th>Actual Time</th><th>Dimensions</th><th>Bd Ft</th><th>Lumber</th><th>Skids</th><th>Cut Date</th><th>Source</th>${withRemove ? '<th></th>' : ''}</tr></thead>
  <tbody>${items.map(item => `<tr>
    <td data-label="Component">${escHtml(item.name)}${item.notched ? ' <em>(Notched)</em>' : ''}${item.notes ? `<div class="notes">${escHtml(item.notes)}</div>` : ''}</td>
    <td data-label="Qty"><strong>${(item.total ?? item.original_total ?? 0).toLocaleString()}</strong></td>
    <td data-label="Est. Time">${item.pcs_per_min > 0 ? formatTime((item.total || 0) / item.pcs_per_min) : '—'}</td>
    <td data-label="Actual Time">${item.stopwatch_elapsed ? formatStopwatch(item.stopwatch_elapsed) : '—'}</td>
    <td data-label="Dimensions">${item.thickness || '—'} x ${item.height || '—'} x ${item.length || '—'}</td>
    <td data-label="Bd Ft">${item.board_footage ? (item.board_footage * (item.per_pallet || 0) * (item.pallet_quantity || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</td>
    <td data-label="Lumber">${lumberCellHtml(item)}</td>
    <td data-label="Skids">${skidsCellHtml(item)}</td>
    <td data-label="Cut Date">${escHtml(fmtDate(item.cut_date))}</td>
    <td data-label="Source">${escHtml(item.pallet_type_name || '—')}</td>
    ${withRemove ? `<td data-label=""><button type="button" data-remove-id="${escHtml(item.id)}" data-remove-name="${escHtml(item.name || 'component')}" data-testid="monthly-remove-${escHtml(item.id)}" style="background:#fff;border:1px solid #dc2626;color:#dc2626;font-size:10px;font-weight:bold;padding:2px 6px;cursor:pointer;text-transform:uppercase;letter-spacing:0.5px">Remove</button></td>` : ''}
  </tr>`).join('')}</tbody>
</table>
<div class="totals">
  <h3>Totals — ${escHtml(m.label)}</h3>
  ${totalEstMinutes > 0 ? `<p>Estimated Time: <strong>${formatTime(totalEstMinutes)}</strong>${missingTime > 0 ? ` <span class="missing">(${missingTime} missing!)</span>` : ''}</p>` : ''}
  ${totalStopwatchSecs > 0 ? `<p>Actual Time: <strong>${formatStopwatch(totalStopwatchSecs)}</strong></p>` : ''}
  ${totalBdFt > 0 ? `<p>Finished Product Board Feet: <strong>${totalBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong>${missingBdFt > 0 ? ` <span class="missing">(${missingBdFt} missing!)</span>` : ''}</p>` : ''}
  ${rawBdFt > 0 ? `<p>Raw Board Footage: <strong>${rawBdFt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</strong></p>` : ''}
  ${cantEntries.length > 0 ? '<h3 style="margin-top:12px">Lumber Used</h3>' + cantEntries.map(e => '<p>' + Math.round(e.total * 100) / 100 + ' — ' + (e.thickness ? e.thickness + '"' : '') + (e.width && e.width !== 'Random' ? ' x ' + e.width + '"' : e.width === 'Random' ? ' x Rndm' : '') + " x " + e.length + "'" + (e.species ? ' ' + escHtml(e.species) : '') + '</p>').join('') : ''}
</div>`;
};

// Two-finger pinch zoom kept working here even though page zoom is disabled app-wide
function PinchZoomBox({ children }) {
  const [scale, setScale] = useState(1);
  const start = useRef(null);
  const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
  return (
    <div
      className="p-3 overflow-auto"
      style={{ touchAction: "pan-x pan-y" }}
      onTouchStart={(e) => { if (e.touches.length === 2) start.current = { d: dist(e.touches), scale }; }}
      onTouchMove={(e) => {
        if (e.touches.length === 2 && start.current) {
          setScale(Math.min(3, Math.max(0.35, start.current.scale * (dist(e.touches) / start.current.d))));
        }
      }}
      onTouchEnd={() => { start.current = null; }}
      data-testid="pinch-zoom-box"
    >
      {/* Fixed-width sheet: zooming scales the whole sheet uniformly — spacing never stretches */}
      <div style={{ transform: `scale(${scale})`, transformOrigin: "top left", width: "760px" }}>
        {children}
      </div>
    </div>
  );
}

export function MonthlyCutHistory({ open, onOpenChange, savedList, listId = null, listName = null, defaultListId = null, onRemoveItem = null }) {
  const [selected, setSelected] = useState({});
  const [confirmRemove, setConfirmRemove] = useState(null);

  const months = useMemo(() => {
    const norm = (lid) => lid || defaultListId;
    const groups = {};
    (savedList || [])
      .filter(i => i.in_history && !i.hidden_from_monthly && (!listId || norm(i.list_id) === listId))
      .forEach(i => {
        const key = monthKeyOf(i);
        if (!key) return;
        (groups[key] = groups[key] || []).push(i);
      });
    return Object.keys(groups).sort().reverse().map(key => {
      const rawItems = groups[key].sort((a, b) => (a.cut_date || "").localeCompare(b.cut_date || ""));
      const m = { key, label: monthLabel(key), rawItems };
      m.html = monthHtml(m);
      m.screenHtml = onRemoveItem ? monthHtml(m, true) : m.html;
      return m;
    });
  }, [savedList, listId, defaultListId, onRemoveItem]);

  const toggleMonth = (key) => setSelected(s => ({ ...s, [key]: !s[key] }));
  const selectedKeys = months.filter(m => selected[m.key]).map(m => m.key);

  const printSelected = () => {
    const chosen = months.filter(m => selected[m.key]);
    if (!chosen.length) return;
    const html = `<!DOCTYPE html><html><head><title>Monthly Cut History</title>
<style>
  body { padding: 20px; margin: 0; }
  ${PRINT_CSS}
  @media print { body { padding: 0; } }
</style></head><body class="pcc-print">
${chosen.map(m => m.html).join('')}
</body></html>`;
    const w = window.open("", "_blank");
    if (!w) return;
    w.opener = null;
    w.document.write(html);
    w.document.close();
    setTimeout(() => { w.focus(); w.print(); }, 300);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-6xl w-[96vw] max-h-[85vh] overflow-y-auto p-3 sm:p-6 [&>button]:hidden" data-testid="monthly-history-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <DialogTitle className="text-lg font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>
              {listId ? `Monthly Cut History${listName ? ` — ${listName}` : ''}` : 'Total Monthly Cut History'}
            </DialogTitle>
            <button onClick={() => onOpenChange(false)} className="p-1.5 -mt-1 -mr-1 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 transition-colors flex-shrink-0" data-testid="monthly-history-close-x">
              <X className="w-6 h-6" />
            </button>
          </div>
        </DialogHeader>
        {months.length === 0 ? (
          <p className="text-sm text-zinc-500 py-6 text-center" data-testid="monthly-history-empty">
            Nothing here yet — components you move to history will be grouped by the month they were cut.
          </p>
        ) : (
          <div className="space-y-4">
            <style>{PRINT_CSS}</style>
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs text-zinc-400">Tick months to include them in the printout.</p>
              <button onClick={printSelected} disabled={selectedKeys.length === 0}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-zinc-800 text-white hover:bg-zinc-900 disabled:opacity-40 flex-shrink-0"
                data-testid="monthly-history-print-btn">
                <Printer className="w-3.5 h-3.5" /> Print Selected{selectedKeys.length > 0 ? ` (${selectedKeys.length})` : ""}
              </button>
            </div>
            {months.map(m => (
              <div key={m.key} className="border border-zinc-200" data-testid={`month-block-${m.key}`}>
                <div className="flex items-center gap-2 px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                  <div
                    className={`w-4 h-4 flex-shrink-0 border-2 flex items-center justify-center cursor-pointer transition-colors ${selected[m.key] ? "bg-orange-600 border-orange-600" : "border-zinc-300 hover:border-zinc-400"}`}
                    onClick={() => toggleMonth(m.key)} data-testid={`month-select-${m.key}`}>
                    {selected[m.key] && <span className="text-white text-[10px] font-bold leading-none">✓</span>}
                  </div>
                  <span className="text-sm font-bold text-zinc-800 flex-1" style={{ fontFamily: "Chivo, sans-serif" }}>{m.label}</span>
                  <span className="text-[11px] text-zinc-400">{m.rawItems.length} item{m.rawItems.length === 1 ? "" : "s"}</span>
                </div>
                <PinchZoomBox>
                  <div className="pcc-print min-w-[900px]" data-testid={`month-print-view-${m.key}`}
                    onClick={(e) => {
                      const btn = e.target.closest('[data-remove-id]');
                      if (btn && onRemoveItem) setConfirmRemove({ id: btn.getAttribute('data-remove-id'), name: btn.getAttribute('data-remove-name') });
                    }}
                    dangerouslySetInnerHTML={{ __html: m.screenHtml }} />
                </PinchZoomBox>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!confirmRemove} onOpenChange={(o) => { if (!o) setConfirmRemove(null); }}>
      <AlertDialogContent className="bg-white border border-zinc-200 rounded-none" data-testid="monthly-remove-confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-xl font-bold text-zinc-900" style={{ fontFamily: "Chivo, sans-serif" }}>Remove From Monthly History?</AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            <strong>{confirmRemove?.name}</strong> will be removed from this monthly cut history only. It stays in every other history on the app.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-none border-zinc-300">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => { onRemoveItem && onRemoveItem(confirmRemove.id); setConfirmRemove(null); }}
            className="bg-red-600 hover:bg-red-700 text-white rounded-none" data-testid="monthly-remove-confirm-btn">Remove</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
